import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import api from '../utils/api';
import {
  Mail, Send, Star, Trash2, Inbox, FileText, Search, Plus, X, ChevronLeft,
  Reply, ReplyAll, Forward, Loader2, Paperclip, RefreshCw,
  ChevronDown, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link as LinkIcon, MailOpen, Image, Code, Eye, ClipboardList,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import GoogleConnectPrompt from '../components/GoogleConnectPrompt';

/* ──────────────────── RecipientInput (자동완성) ──────────────────── */
function RecipientInput({ label, value, onChange, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [dropRect, setDropRect] = useState(null);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const parts = value ? value.split(',') : [];
  const lastPart = (parts[parts.length - 1] || '').trim();

  // 드롭다운 위치를 wrapRef 기준으로 계산 (portal 렌더링용)
  useLayoutEffect(() => {
    if (!showDrop || !wrapRef.current) return;
    const update = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setDropRect({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showDrop]);

  useEffect(() => {
    if (!lastPart) { setSuggestions([]); setShowDrop(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/google/contacts?q=${encodeURIComponent(lastPart)}`);
        setSuggestions(data.slice(0, 10));
        setShowDrop(data.length > 0);
      } catch { setSuggestions([]); setShowDrop(false); }
    }, 200);
  }, [lastPart]);

  useEffect(() => {
    function onDown(e) {
      if (!wrapRef.current?.contains(e.target)) setShowDrop(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  function select(contact) {
    const prev = parts.slice(0, -1).join(', ');
    let addition;
    if (contact.type === 'group' && contact.emails?.length) {
      // 그룹 → 멤버 이메일 전체 추가
      addition = contact.emails.join(', ');
    } else {
      addition = `${contact.label} <${contact.email}>`;
    }
    onChange(prev ? `${prev}, ${addition}, ` : `${addition}, `);
    setSuggestions([]); setShowDrop(false);
    inputRef.current?.focus();
  }

  const dropEl = showDrop && suggestions.length > 0 && dropRect && createPortal(
    <div
      style={{ position: 'fixed', top: dropRect.top, left: dropRect.left, width: dropRect.width, zIndex: 9999 }}
      className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
    >
      {suggestions.map((c, i) => (
        <button
          key={c.type === 'group' ? `g-${c.label}` : `${c.email}-${i}`}
          type="button"
          onMouseDown={e => { e.preventDefault(); select(c); }}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left"
        >
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            c.type === 'group' ? 'bg-blue-100 text-blue-600' :
            c.type === 'partner' ? 'bg-green-100 text-green-600' :
            'bg-[#e0005e]/10 text-[#e0005e]'
          }`}>
            {c.type === 'group' ? '팀' : (c.name || '?')[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">{c.label}</p>
            <p className="text-xs text-gray-400 truncate">
              {c.type === 'group' ? `${c.count}명` : c.email}
            </p>
          </div>
          {c.type === 'partner' && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded flex-shrink-0">거래처</span>}
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef} className="flex items-center border-b border-gray-200 px-4 py-2.5 gap-3 flex-shrink-0">
      <span className="text-sm text-gray-400 w-20 flex-shrink-0">{label}</span>
      <input
        ref={inputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 text-sm text-gray-800 bg-transparent focus:outline-none placeholder-gray-300"
      />
      {dropEl}
    </div>
  );
}

const LABELS = [
  { id: 'INBOX', label: '받은편지함', icon: Inbox },
  { id: 'STARRED', label: '별표편지함', icon: Star },
  { id: 'SENT', label: '보낸편지함', icon: Send },
  { id: 'DRAFT', label: '임시보관함', icon: FileText },
  { id: 'TRASH', label: '휴지통', icon: Trash2 },
];

function parseFrom(from = '') {
  const m = from.match(/^"?([^"<]+)"?\s*<?([^>]*)>?$/);
  return { name: (m?.[1] || from).trim(), email: (m?.[2] || '').trim() };
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate())
    return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  if (diff < 7 * 86400000)
    return d.toLocaleDateString('ko-KR', { weekday: 'short' });
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

/* ──────────────────── EmailBody (iframe) ──────────────────── */
function EmailBody({ html, inlineImages = {} }) {
  const iframeRef = useRef(null);
  const blobUrls = useRef([]);

  useEffect(() => {
    return () => { blobUrls.current.forEach(u => URL.revokeObjectURL(u)); };
  }, []);

  useEffect(() => {
    if (!html) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const hasCid = html.includes('cid:');
    const cidKeys = hasCid ? Object.keys(inlineImages) : [];

    async function render(resolvedHtml) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      // DOMPurify: 외부 이미지 URL 허용 (ALLOW_UNKNOWN_PROTOCOLS for cid already replaced)
      const safe = DOMPurify.sanitize(resolvedHtml, {
        ADD_ATTR: ['target', 'style'],
        FORCE_BODY: true,
        // data: URI 허용 (인라인 이미지 표시용)
        ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      });
      doc.open();
      doc.write(`<!DOCTYPE html><html><head>
        <meta charset="utf-8">
        <style>
          body{font-family:Arial,sans-serif;font-size:14px;color:#222;background:#fff;margin:0;padding:12px;word-break:break-word;}
          a{color:#1a73e8;}img{max-width:100%;height:auto;}*{box-sizing:border-box;}
        </style>
      </head><body>${safe}</body></html>`);
      doc.close();
      const resize = () => { try { iframe.style.height = (doc.body.scrollHeight + 24) + 'px'; } catch {} };
      setTimeout(resize, 200);
      iframe.onload = resize;
    }

    if (!hasCid || cidKeys.length === 0) {
      render(html);
      return;
    }

    // CID 인라인 이미지를 blob URL로 교체
    (async () => {
      let resolved = html;
      await Promise.all(cidKeys.map(async cid => {
        const { attachmentId, mimeType, messageId } = inlineImages[cid];
        try {
          const res = await api.get(
            `/google/gmail/messages/${messageId}/attachments/${attachmentId}`,
            { params: { mimeType }, responseType: 'blob' }
          );
          const blob = new Blob([res.data], { type: mimeType });
          const url = URL.createObjectURL(blob);
          blobUrls.current.push(url);
          resolved = resolved.replace(new RegExp(`cid:${cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'), url);
        } catch (_) {}
      }));
      render(resolved);
    })();
  }, [html, inlineImages]);

  return <iframe ref={iframeRef} title="email-body" sandbox="allow-same-origin allow-popups" className="w-full border-0 min-h-[80px]" style={{ height: '200px' }} />;
}

/* ──────────────────── Compose Editor ──────────────────── */
function EmailEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, code: false, codeBlock: false, blockquote: false }),
      Underline,
      TextStyle,
      Link.configure({ openOnClick: false }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value || '', false);
    }
  }, [value]);

  function addLink() {
    const url = prompt('링크 URL을 입력하세요:');
    if (!url) return;
    if (editor.state.selection.empty) {
      editor.chain().focus().setLink({ href: url }).insertContent(url).run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }

  const btn = (active, onClick, children, title) => (
    <button
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
    >{children}</button>
  );

  return (
    <div className="flex flex-col bg-white">
      {/* 툴바 — sticky로 스크롤해도 상단 고정 */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 sticky top-0 bg-white z-10">
        {btn(editor?.isActive('bold'), () => editor?.chain().focus().toggleBold().run(), <Bold size={13} />, '굵게')}
        {btn(editor?.isActive('italic'), () => editor?.chain().focus().toggleItalic().run(), <Italic size={13} />, '기울임')}
        {btn(editor?.isActive('underline'), () => editor?.chain().focus().toggleUnderline().run(), <UnderlineIcon size={13} />, '밑줄')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {btn(editor?.isActive('bulletList'), () => editor?.chain().focus().toggleBulletList().run(), <List size={13} />, '목록')}
        {btn(editor?.isActive('orderedList'), () => editor?.chain().focus().toggleOrderedList().run(), <ListOrdered size={13} />, '번호목록')}
        <div className="w-px h-4 bg-gray-300 mx-1" />
        {btn(editor?.isActive('link'), addLink, <LinkIcon size={13} />, '링크')}
        {editor?.isActive('link') && btn(false, () => editor?.chain().focus().unsetLink().run(), <X size={11} />, '링크제거')}
      </div>
      {/* 본문 */}
      <EditorContent
        editor={editor}
        className="px-4 py-3 [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[180px] [&_.ProseMirror_p]:my-1 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_a]:text-blue-600 [&_.ProseMirror_a]:underline"
        style={{ fontSize: 14, color: '#222' }}
      />
    </div>
  );
}

/* ──────────────────── SignatureModal ──────────────────── */
function SignatureModal({ sendAs, onSave, onClose }) {
  const [selectedEmail, setSelectedEmail] = useState(sendAs[0]?.sendAsEmail || '');
  const [sigs, setSigs] = useState(() =>
    sendAs.map(s => ({ email: s.sendAsEmail, name: s.displayName || s.sendAsEmail, sig: s.signature || '' }))
  );
  const [tab, setTab] = useState('image'); // 'image' | 'html' | 'editor'
  const [imgUploading, setImgUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const cur = sigs.find(s => s.email === selectedEmail) || sigs[0];
  const curIdx = sigs.findIndex(s => s.email === selectedEmail);
  function updateSig(val) {
    setSigs(prev => prev.map((s, i) => i === curIdx ? { ...s, sig: val } : s));
  }

  // 이미지 업로드 → <img> 태그로 서명에 삽입
  async function handleImageUpload(file) {
    if (!file) return;
    setImgUploading(true);
    try {
      const form = new FormData();
      form.append('image', file);
      const { data } = await api.post('/google/gmail/signature-image', form);
      // 이미지만 서명으로 설정 (기존 이미지 교체)
      updateSig(`<img src="${data.url}" alt="서명" style="max-width:400px;height:auto;" />`);
    } catch (e) {
      console.error('[서명 업로드]', e.response?.data || e.message);
      alert('업로드 실패: ' + (e.response?.data?.message || e.message));
    }
    finally { setImgUploading(false); }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await Promise.allSettled(sigs.map(s =>
        api.patch(`/google/gmail/send-as/${encodeURIComponent(s.email)}`, { signature: s.sig })
      ));
      onSave(sigs);
      onClose();
    } catch (e) { alert('저장 실패: ' + e.message); }
    finally { setSaving(false); }
  }

  // 서명 미리보기용 HTML (2줄 띄우기 포함)
  const previewHtml = `<div style="font-size:14px;color:#333"><br><br>${cur?.sig || ''}</div>`;

  const tabBtn = (key, icon, label) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        tab === key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-700'
      }`}
    >{icon}{label}</button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-xl shadow-2xl w-[600px] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">서명 관리</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>

        {/* 계정 선택 */}
        {sendAs.length > 1 && (
          <div className="px-5 py-3 border-b border-gray-100 flex gap-2">
            {sendAs.map(s => (
              <button
                key={s.sendAsEmail}
                onClick={() => setSelectedEmail(s.sendAsEmail)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedEmail === s.sendAsEmail ? 'bg-[#e0005e] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >{s.displayName || s.sendAsEmail}</button>
            ))}
          </div>
        )}

        {/* 탭 */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-gray-100">
          {tabBtn('image', <Image size={12} />, '이미지')}
          {tabBtn('html', <Code size={12} />, 'HTML')}
          {tabBtn('preview', <Eye size={12} />, '미리보기')}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'image' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">이미지 파일을 업로드하면 서명으로 사용됩니다. (본문과 2줄 자동 간격)</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e.target.files[0])} />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={imgUploading}
                className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors w-full justify-center"
              >
                {imgUploading ? <Loader2 size={14} className="animate-spin" /> : <Image size={14} />}
                {imgUploading ? '업로드 중...' : '이미지 선택 (PNG/JPG)'}
              </button>
              {cur?.sig?.includes('<img') && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-xs text-gray-400 mb-2">현재 서명 이미지</p>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cur.sig) }} />
                </div>
              )}
              {cur?.sig && !cur.sig.includes('<img') && (
                <p className="text-xs text-gray-400">현재 서명: 텍스트 서명 사용 중 (HTML 탭에서 확인)</p>
              )}
            </div>
          )}

          {tab === 'html' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-400">HTML로 서명을 직접 작성합니다. 이미지는 {'<img src="URL">'} 형태로 삽입하세요.</p>
              <textarea
                value={cur?.sig || ''}
                onChange={e => updateSig(e.target.value)}
                rows={10}
                placeholder='<p>홍길동 대리 | 핑크 주식회사</p>&#10;<p>📞 010-0000-0000 | ✉️ email@company.com</p>&#10;또는 <img src="https://..." /> 이미지 태그'
                className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg p-3 focus:outline-none focus:border-gray-400 resize-none font-mono leading-relaxed"
              />
            </div>
          )}

          {tab === 'preview' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-400">본문과 2줄 간격 후 서명이 이렇게 보입니다.</p>
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <p className="text-sm text-gray-700 mb-1">안녕하세요,</p>
                <p className="text-sm text-gray-700">본문 내용...</p>
                <EmailBody html={previewHtml} />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">취소</button>
          <button
            onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={{ background: '#e0005e', color: '#ffffff' }}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : null} 저장
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────── DrivePicker Modal ──────────────────── */
// stack item: { id, name, driveId? }  driveId 있으면 공유드라이브 내부
function DrivePicker({ onSelect, onClose }) {
  // null = 최상위(My Drive + 공유드라이브 목록)
  const [stack, setStack] = useState([]);
  const [items, setItems] = useState([]);   // 현재 폴더 항목
  const [drives, setDrives] = useState([]); // 공유 드라이브 목록 (최상위에서 사용)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  const atRoot = stack.length === 0;
  const current = stack[stack.length - 1] || null;

  useEffect(() => { loadRoot(); }, []);

  async function loadRoot() {
    setLoading(true); setError('');
    try {
      const [myDriveRes, drivesRes] = await Promise.all([
        api.get('/google/drive/files?folderId=root'),
        api.get('/google/drive/drives'),
      ]);
      setItems(myDriveRes.data.files || []);
      setDrives(drivesRes.data.drives || []);
    } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }

  async function loadFolder(folderId, driveId) {
    setLoading(true); setError('');
    try {
      const url = driveId
        ? `/google/drive/drives/${driveId}/files?folderId=${folderId}`
        : `/google/drive/files?folderId=${folderId}`;
      const { data } = await api.get(url);
      setItems(data.files || []);
    } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!q.trim()) { goBack(); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.get(`/google/drive/search?q=${encodeURIComponent(q)}`);
      setStack(s => [...s, { id: '__search__', name: `"${q}" 검색결과` }]);
      setItems(data.files || []);
    } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }

  function openFolder(f, driveId) {
    setStack(s => [...s, { id: f.id, name: f.name, driveId }]);
    loadFolder(f.id, driveId);
  }

  function openDrive(d) {
    setStack(s => [...s, { id: d.id, name: d.name, driveId: d.id }]);
    loadFolder(d.id, d.id);
  }

  function goBack() {
    if (stack.length === 0) return;
    const newStack = stack.slice(0, -1);
    setStack(newStack);
    setQ('');
    if (newStack.length === 0) loadRoot();
    else {
      const prev = newStack[newStack.length - 1];
      loadFolder(prev.id, prev.driveId);
    }
  }

  function fmt(bytes) {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + 'KB';
    return (bytes / 1024 / 1024).toFixed(1) + 'MB';
  }

  const isFolder = f => f.mimeType === 'application/vnd.google-apps.folder';
  const driveId = current?.driveId;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[520px] max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {stack.length > 0 && (
              <button onClick={goBack} className="p-1 rounded hover:bg-gray-100 text-gray-500"><ChevronLeft size={16} /></button>
            )}
            <span className="font-semibold text-gray-800 text-sm">
              {atRoot ? 'Google Drive' : current.name}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        {/* 검색 */}
        <div className="px-4 py-2 border-b border-gray-200">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="파일명 검색..."
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-gray-400" />
            <button type="submit" className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700">검색</button>
          </form>
        </div>
        {/* 목록 */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-24"><Loader2 className="animate-spin text-gray-400" size={18} /></div>
          ) : error ? (
            <p className="text-center text-red-400 text-sm py-8">{error}</p>
          ) : (
            <>
              {/* 최상위: 공유 드라이브 목록 */}
              {atRoot && drives.map(d => (
                <button key={d.id} onClick={() => openDrive(d)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-b border-gray-100">
                  <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center flex-shrink-0 text-base">🗂️</div>
                  <p className="flex-1 text-sm font-medium text-gray-700 truncate">{d.name}</p>
                  <ChevronLeft size={14} className="text-gray-400 rotate-180 flex-shrink-0" />
                </button>
              ))}
              {/* 파일/폴더 목록 */}
              {items.length === 0 && !atRoot ? (
                <p className="text-center text-gray-400 text-sm py-8">파일이 없습니다</p>
              ) : items.map(f => (
                <button key={f.id}
                  onClick={() => isFolder(f) ? openFolder(f, driveId) : onSelect(f)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-b border-gray-100">
                  {isFolder(f) ? (
                    <div className="w-8 h-8 rounded bg-yellow-50 flex items-center justify-center flex-shrink-0 text-base">📁</div>
                  ) : f.thumbnailLink ? (
                    <img src={f.thumbnailLink} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-blue-50 flex items-center justify-center flex-shrink-0"><Paperclip size={14} className="text-blue-500" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${isFolder(f) ? 'font-medium text-gray-700' : 'text-gray-800'}`}>{f.name}</p>
                    {!isFolder(f) && <p className="text-xs text-gray-400">{fmt(f.size)}</p>}
                  </div>
                  {isFolder(f) && <ChevronLeft size={14} className="text-gray-400 rotate-180 flex-shrink-0" />}
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ──────────────────── 견적서 HTML 생성 헬퍼 ──────────────────── */
const QUOTE_COMPANY = {
  bizNum: '688-07-02108', name: '핑크페이퍼', ceo: '김태연 · 여은영',
  addr: '서울특별시 노원구 노원로22길 71 306동 1501호',
  bizType: '서비스', bizItem: '비디오물제작업 / 시각디자인업 / 공연기획업',
  tel: '010-2726-3790',
};
function fmtNum(n) { return Number(n || 0).toLocaleString('ko-KR'); }

async function buildQuoteFile(quote) {
  const items = Array.isArray(quote.items) ? quote.items : [];
  const rowsHtml = items.map(r => `<tr>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.group || ''}</td>
    <td style="border:1px solid #000;font-size:11px;padding:3px;">${r.detail || ''}</td>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.spec || ''}</td>
    <td style="border:1px solid #000;text-align:right;font-size:11px;padding:3px;">${fmtNum(r.price)}</td>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.qty}</td>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.unit || ''}</td>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.count}</td>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.countUnit || '회'}</td>
    <td style="border:1px solid #000;text-align:right;font-size:11px;padding:3px;">${fmtNum(r.totalPrice)}</td>
    <td style="border:1px solid #000;text-align:center;font-size:11px;padding:3px;">${r.note || ''}</td>
  </tr>`).join('');
  const dateStr = quote.quoteDate
    ? `${quote.quoteDate.slice(0,4)}년 ${Number(quote.quoteDate.slice(5,7))}월 ${Number(quote.quoteDate.slice(8,10))}일`
    : '';
  const html = `<!DOCTYPE html><html lang="ko"><head><meta charset="UTF-8"/><title>견적서</title>
<style>body{font-family:'Malgun Gothic',sans-serif;font-size:12px;margin:20px;}
table{border-collapse:collapse;}th,td{border:1px solid #000;padding:4px 6px;}</style></head>
<body>
<h1 style="text-align:center;letter-spacing:8px;margin-bottom:16px;">견 적 서</h1>
<table style="width:100%;margin-bottom:8px;">
<tr><td style="background:#f0f0f0;font-weight:bold;width:100px;">수 신 처</td><td>${quote.recipientName||''}</td>
<td style="background:#f0f0f0;font-weight:bold;width:60px;">날 짜</td><td>${dateStr}</td></tr>
${quote.recipientBizNum?`<tr><td style="background:#f0f0f0;font-weight:bold;">사업자번호</td><td>${quote.recipientBizNum}</td><td style="background:#f0f0f0;font-weight:bold;">유효기간</td><td>발행 후 ${quote.validDays||7}일</td></tr>`:''}
</table>
<table style="width:100%;margin-bottom:8px;">
<tr><td rowspan="4" style="background:#f0f0f0;font-weight:bold;text-align:center;width:60px;">공급자</td>
<td><b>사업자번호</b> ${QUOTE_COMPANY.bizNum}</td><td><b>상 호</b> ${QUOTE_COMPANY.name}</td><td><b>대표자</b> ${QUOTE_COMPANY.ceo}</td></tr>
<tr><td colspan="3"><b>소 재 지</b> ${QUOTE_COMPANY.addr}</td></tr>
<tr><td><b>업 태</b> ${QUOTE_COMPANY.bizType}</td><td colspan="2"><b>종 목</b> ${QUOTE_COMPANY.bizItem}</td></tr>
<tr><td colspan="3"><b>전 화</b> ${QUOTE_COMPANY.tel}</td></tr>
</table>
<table style="width:100%;margin-bottom:4px;">
<thead><tr style="background:#f0f0f0;"><th>항목</th><th>내역</th><th>규격</th><th>단가(원)</th><th>수량</th><th>단위</th><th>횟수</th><th>단위</th><th>최종산출가</th><th>비고</th></tr></thead>
<tbody>${rowsHtml}</tbody></table>
<table style="float:right;margin-bottom:16px;">
<tr><td style="background:#f0f0f0;font-weight:bold;width:140px;text-align:center;">합 계</td><td style="text-align:right;min-width:120px;">${fmtNum(quote.subtotal)}원</td></tr>
<tr><td style="background:#f0f0f0;font-weight:bold;text-align:center;">부가세 (10%)</td><td style="text-align:right;">${fmtNum(quote.vat)}원</td></tr>
<tr><td style="background:#f0f0f0;font-weight:bold;text-align:center;">최 종 견 적</td><td style="text-align:right;font-weight:bold;">${fmtNum(quote.total)}원 <span style="font-size:10px;">(만원 이하 절사)</span></td></tr>
</table>
<div style="clear:both;font-size:12px;line-height:2;">
<div><b style="display:inline-block;width:80px;">유효기간</b>발행일로부터 ${quote.validDays||7}일</div>
<div><b style="display:inline-block;width:80px;">계좌번호</b>${quote.bankInfo||''}</div>
${quote.memo?`<div><b style="display:inline-block;width:80px;">비 고</b>${quote.memo}</div>`:''}
</div>
</body></html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const fname = `견적서_${quote.recipientName||'수신처없음'}_${quote.quoteDate||''}.html`;
  return new File([blob], fname, { type: 'text/html' });
}

/* ──────────────────── QuotePicker ──────────────────── */
function QuotePicker({ onSelect, onClose }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/quotations').then(r => { setList(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = list.filter(item =>
    !q || (item.recipientName || '').includes(q) || (item.quoteNumber || '').includes(q)
  );

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-96 max-h-[520px] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">견적서 선택</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="px-4 py-2 border-b border-gray-100">
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="검색..."
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand"
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-6 text-center text-xs text-gray-400">불러오는 중...</div>}
          {!loading && filtered.length === 0 && <div className="p-6 text-center text-xs text-gray-400">견적서가 없습니다.</div>}
          {filtered.map(item => (
            <button
              key={item.id}
              onClick={async () => {
                try {
                  const { data } = await api.get(`/quotations/${item.id}`);
                  onSelect(data);
                } catch { onSelect(item); }
              }}
              className="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">{item.recipientName || '수신처 미입력'}</span>
                <span className="text-xs text-gray-400">{item.quoteDate}</span>
              </div>
              <div className="text-xs text-gray-400">{item.quoteNumber} · {Number(item.total||0).toLocaleString('ko-KR')}원</div>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ──────────────────── ComposeView ──────────────────── */
function ComposeView({ sendAs: initialSendAs, onClose, onSend, initial = {}, onOpenSigModal }) {
  const [sendAs, setSendAs] = useState(initialSendAs);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [addGroupForm, setAddGroupForm] = useState({ email: '', name: '' });
  const [addingGroup, setAddingGroup] = useState(false);
  const quotedBody = initial.quotedBody || '';
  const [form, setForm] = useState({
    from: sendAs[0]?.sendAsEmail || '',
    to: initial.to || '',
    cc: initial.cc || '',
    bcc: '',
    subject: initial.subject || '',
    body: initial.body || '',
    inReplyTo: initial.inReplyTo || '',
    references: initial.references || '',
    threadId: initial.threadId || '',
  });
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [showQuotePicker, setShowQuotePicker] = useState(false);
  const fileInputRef = useRef(null);
  const lastSavedRef = useRef({ subject: '', body: '', to: '' });
  const formRef = useRef(form);
  formRef.current = form;
  const sigInsertedRef = useRef(false); // 서명 자동삽입 최초 1회 플래그
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // 부모 sendAs prop이 로드되면 local state에 sync (race condition 방지)
  useEffect(() => {
    if (initialSendAs.length > 0 && sendAs.length === 0) {
      setSendAs(initialSendAs);
    }
  }, [initialSendAs.length]);

  // 초기 서명 자동 삽입 — sendAs 로드 후 최초 1회만 실행 (이후 재실행 시 사용자 입력 덮어쓰기 방지)
  useEffect(() => {
    const sa = sendAs.find(s => s.isDefault) || sendAs[0];
    if (!sa || sigInsertedRef.current) return;
    sigInsertedRef.current = true;
    if (sa.signature && !initial.body) {
      setForm(f => ({
        ...f,
        from: sa.sendAsEmail,
        body: `<p></p><p></p>${sa.signature}`,
      }));
    } else {
      setForm(f => ({ ...f, from: sa.sendAsEmail }));
    }
  }, [sendAs]);

  // 자동 임시저장 (30초) — formRef로 최신값 참조, form을 dep에 넣지 않아 타이핑해도 리셋 안됨
  useEffect(() => {
    const interval = setInterval(async () => {
      const f = formRef.current;
      if (!f.subject && !f.to && !f.body) return;
      const snap = { subject: f.subject, body: f.body, to: f.to };
      if (JSON.stringify(snap) === JSON.stringify(lastSavedRef.current)) return;
      setSaveStatus('저장 중...');
      try {
        await api.post('/google/gmail/drafts', f);
        lastSavedRef.current = snap;
        setSaveStatus('임시저장됨');
        setTimeout(() => setSaveStatus(''), 3000);
      } catch (e) {
        setSaveStatus('저장 실패: ' + (e.response?.data?.message || e.message));
        setTimeout(() => setSaveStatus(''), 4000);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []); // 마운트 1회만

  // sendAs 선택 시 서명 자동 삽입
  function handleFromChange(email) {
    const sa = sendAs.find(s => s.sendAsEmail === email);
    const sig = sa?.signature ? `<br><br><div style="color:#777;font-size:13px">--<br>${sa.signature}</div>` : '';
    up('from', email);
    if (sig && !form.body.includes('--')) up('body', (form.body || '') + sig);
  }

  async function handleAddGroup() {
    const email = addGroupForm.email.trim();
    const name = addGroupForm.name.trim();
    if (!email) return;
    setAddingGroup(true);
    try {
      await api.post('/google/gmail/send-as', { email, displayName: name || email });
      const newEntry = { sendAsEmail: email, displayName: name || email, type: 'group', signature: '' };
      setSendAs(prev => [...prev, newEntry]);
      up('from', email);
      setShowAddGroup(false);
      setAddGroupForm({ email: '', name: '' });
    } catch (e) {
      alert('그룹 주소 추가 실패: ' + (e.response?.data?.message || e.message));
    } finally { setAddingGroup(false); }
  }

  function handleFileSelect(e) {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    e.target.value = '';
  }

  function handleDriveSelect(driveFile) {
    setShowDrivePicker(false);
    setAttachments(prev => [...prev, {
      _isDrive: true,
      id: driveFile.id,
      name: driveFile.name,
      mimeType: driveFile.mimeType || 'application/octet-stream',
      size: driveFile.size,
    }]);
  }

  function removeAttachment(idx) {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSend() {
    if (!form.to.trim()) { alert('받는 사람을 입력해주세요.'); return; }
    setSending(true);
    try {
      const finalBody = quotedBody
        ? `${form.body || ''}<br><br>${quotedBody}`
        : (form.body || '');
      const localFiles = attachments.filter(a => !a._isDrive);
      const driveFiles = attachments.filter(a => a._isDrive);
      const fd = new FormData();
      Object.entries({ ...form, body: finalBody }).forEach(([k, v]) => { if (v) fd.append(k, v); });
      localFiles.forEach(f => fd.append('attachments', f));
      if (driveFiles.length > 0) fd.append('driveAttachments', JSON.stringify(driveFiles));
      await api.post('/google/gmail/send', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSend('SENT');
    } catch (e) { alert('전송 실패: ' + (e.response?.data?.message || e.message)); }
    finally { setSending(false); }
  }

  const fieldRow = (label, key, placeholder, opts = {}) => (
    <div className="flex items-center border-b border-gray-200 px-4 py-2.5 gap-3 flex-shrink-0">
      <span className="text-sm text-gray-400 w-20 flex-shrink-0">{label}</span>
      <input
        value={form[key] || ''}
        onChange={e => up(key, e.target.value)}
        placeholder={placeholder}
        className="flex-1 text-sm text-gray-800 bg-transparent focus:outline-none placeholder-gray-300"
        {...opts}
      />
    </div>
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-white">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0 bg-gray-50">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-800">새 메일 작성</h2>
          {saveStatus && <span className="text-xs text-gray-400">{saveStatus}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">취소</button>
          <button
            onClick={handleSend} disabled={sending}
            className="flex items-center gap-1.5 px-5 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            style={{ background: '#e0005e', color: '#ffffff' }}
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            보내기
          </button>
        </div>
      </div>

      {/* 보내는이 */}
      <div className="flex flex-col border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center px-4 py-2.5 gap-3">
          <span className="text-sm text-gray-400 w-20 flex-shrink-0">보내는이</span>
          <select
            value={form.from}
            onChange={e => handleFromChange(e.target.value)}
            className="flex-1 text-sm text-gray-800 bg-transparent focus:outline-none cursor-pointer"
          >
            {sendAs.map(s => (
              <option key={s.sendAsEmail} value={s.sendAsEmail}>
                {s.displayName ? `${s.displayName} (${s.sendAsEmail})` : s.sendAsEmail}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowAddGroup(v => !v)}
            title="그룹 주소 추가"
            className="text-xs text-gray-400 hover:text-[#e0005e] border-l border-gray-200 pl-3 flex-shrink-0"
          >+ 그룹</button>
          <button
            onClick={onOpenSigModal}
            className="text-xs text-gray-400 hover:text-gray-600 border-l border-gray-200 pl-3 flex-shrink-0"
          >서명 관리</button>
        </div>
        {showAddGroup && (
          <div className="flex items-center gap-2 px-4 pb-2.5 pl-[calc(5rem+1rem)]">
            <input
              value={addGroupForm.email}
              onChange={e => setAddGroupForm(f => ({ ...f, email: e.target.value }))}
              placeholder="그룹 이메일 (예: team@pinkpaper.kr)"
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#e0005e]"
            />
            <input
              value={addGroupForm.name}
              onChange={e => setAddGroupForm(f => ({ ...f, name: e.target.value }))}
              placeholder="표시 이름 (예: 신바나팀)"
              className="w-36 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#e0005e]"
            />
            <button
              onClick={handleAddGroup} disabled={addingGroup || !addGroupForm.email}
              className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40"
              style={{ background: '#e0005e', color: '#fff' }}
            >{addingGroup ? '추가 중...' : '추가'}</button>
            <button onClick={() => setShowAddGroup(false)} className="text-gray-400 hover:text-gray-600 text-sm">취소</button>
          </div>
        )}
      </div>

      {/* 받는이 */}
      <RecipientInput label="받는이" value={form.to} onChange={v => up('to', v)} placeholder="이름 또는 이메일" />

      {/* 참조 + BCC 토글 버튼 */}
      <div className="relative">
        <RecipientInput label="참조" value={form.cc} onChange={v => up('cc', v)} placeholder="참조 (CC)" />
        <button
          onClick={() => setShowBcc(v => !v)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
        >
          {showBcc ? '숨은참조 숨기기' : '숨은참조'}
        </button>
      </div>

      {/* 숨은참조 (BCC) */}
      {showBcc && <RecipientInput label="숨은참조" value={form.bcc} onChange={v => up('bcc', v)} placeholder="숨은참조 (BCC)" />}

      {/* 제목 */}
      <div className="flex items-center border-b border-gray-200 px-4 py-2.5 gap-3 flex-shrink-0">
        <span className="text-sm text-gray-400 w-20 flex-shrink-0">제목</span>
        <input
          value={form.subject || ''}
          onChange={e => up('subject', e.target.value)}
          placeholder="제목"
          className="flex-1 text-sm font-medium text-gray-800 bg-transparent focus:outline-none placeholder-gray-300"
        />
      </div>

      {/* 첨부파일 */}
      <div className="border-b border-gray-200 px-4 py-2 flex flex-wrap items-center gap-2 flex-shrink-0">
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs text-gray-500 transition-colors"
        >
          <Paperclip size={12} /> 컴퓨터
        </button>
        <button
          type="button"
          onClick={() => setShowDrivePicker(true)}
          style={{ color: '#ffffff', background: '#4b5563' }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-colors hover:opacity-90"
        >
          <Image size={12} /> Drive
        </button>
        <button
          type="button"
          onClick={() => setShowQuotePicker(true)}
          style={{ color: '#ffffff', background: '#e0005e' }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-colors hover:opacity-90"
        >
          <ClipboardList size={12} /> 견적서
        </button>
        {attachments.map((f, i) => (
          <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">
            {f._isDrive ? <Image size={10} className="text-blue-500" /> : <Paperclip size={10} />}
            {f.name}
            {f.size ? <span className="text-gray-400">({(f.size / 1024).toFixed(0)}KB)</span> : null}
            <button onClick={() => removeAttachment(i)} className="text-gray-400 hover:text-red-500 ml-0.5"><X size={10} /></button>
          </span>
        ))}
      </div>

      {/* 본문 에디터 + 원문 — 하나의 스크롤 영역 */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white flex flex-col">
        <EmailEditor value={form.body} onChange={v => up('body', v)} />
        {quotedBody && (
          <div className="border-t border-gray-100 px-4 pb-4">
            <EmailBody html={quotedBody} />
          </div>
        )}
      </div>

      {showDrivePicker && <DrivePicker onSelect={handleDriveSelect} onClose={() => setShowDrivePicker(false)} />}
      {showQuotePicker && (
        <QuotePicker
          onSelect={async (quote) => {
            setShowQuotePicker(false);
            const file = await buildQuoteFile(quote);
            setAttachments(prev => [...prev, file]);
          }}
          onClose={() => setShowQuotePicker(false)}
        />
      )}
    </div>
  );
}

/* ──────────────────── MessageCard ──────────────────── */
function MessageCard({ msg, defaultOpen, onReply, onReplyAll }) {
  const [open, setOpen] = useState(defaultOpen);
  const from = parseFrom(msg.from);

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setOpen(v => !v)}>
        <div className="w-8 h-8 rounded-full bg-[#e0005e]/20 text-[#e0005e] flex items-center justify-center text-sm font-bold flex-shrink-0">
          {(from.name || from.email || '?')[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-200">{from.name || from.email}</p>
          {!open && <p className="text-xs text-gray-500 truncate">{msg.snippet}</p>}
          {open && <p className="text-[11px] text-gray-500">받는 사람: {msg.to}</p>}
        </div>
        <span className="text-[11px] text-gray-500 flex-shrink-0">{fmtDate(msg.date)}</span>
        <div className="flex gap-1">
          <button onClick={e => { e.stopPropagation(); onReply(); }} className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-gray-300" title="답장"><Reply size={12} /></button>
          <button onClick={e => { e.stopPropagation(); onReplyAll(); }} className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-gray-300" title="전체답장"><ReplyAll size={12} /></button>
          <ChevronDown size={14} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4">
          {msg.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3 pt-2 border-t border-gray-800">
              {msg.attachments.map(a => (
                <a
                  key={a.attachmentId}
                  href={`/api/google/gmail/messages/${a.messageId}/attachments/${a.attachmentId}?filename=${encodeURIComponent(a.filename)}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-xs text-gray-300 transition-colors"
                >
                  <Paperclip size={11} />{a.filename}
                  <span className="text-gray-500">({Math.round((a.size || 0) / 1024)}KB)</span>
                </a>
              ))}
            </div>
          )}
          {msg.body
            ? <EmailBody html={msg.body} inlineImages={msg.inlineImages || {}} />
            : <p className="text-gray-500 text-sm py-2">(내용 없음)</p>
          }
        </div>
      )}
    </div>
  );
}

/* ──────────────────── GmailPage ──────────────────── */
export default function GmailPage() {
  const [connected, setConnected] = useState(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [activeLabel, setActiveLabel] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageDetail, setMessageDetail] = useState(null);
  const [messageLoading, setMessageLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [compose, setCompose] = useState(null); // null | compose initial data
  const [sendAs, setSendAs] = useState([]);
  const [showSigModal, setShowSigModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/google/status').then(r => {
      setConnected(r.data.connected);
      setNeedsReconnect(r.data.needsReconnect || false);
      if (r.data.connected) {
        loadMessages();
        api.get('/google/gmail/send-as').then(r2 => setSendAs(r2.data || [])).catch(() => {});
      }
    }).catch(() => setConnected(false));
  }, []);

  const loadMessages = useCallback(async (label = activeLabel, q = searchQ, token = null) => {
    if (!token) setLoading(true); else setLoadingMore(true);
    try {
      const r = await api.get('/google/gmail/messages', { params: { label, q, pageToken: token } });
      if (token) setMessages(prev => [...prev, ...r.data.messages]);
      else { setMessages(r.data.messages); setSelectedMessage(null); setMessageDetail(null); }
      setNextPageToken(r.data.nextPageToken);
    } catch (e) {
      if (e.response?.status === 401) setConnected(false);
    } finally { setLoading(false); setLoadingMore(false); setRefreshing(false); }
  }, [activeLabel, searchQ]);

  useEffect(() => { if (connected) loadMessages(activeLabel); }, [activeLabel, connected]);

  async function openMessage(msg) {
    setSelectedMessage(msg);
    setMessageLoading(true);
    try {
      const r = await api.get(`/google/gmail/messages/${msg.id}`);
      setMessageDetail(r.data);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, unread: false } : m));
    } catch { setMessageDetail(null); }
    finally { setMessageLoading(false); }
  }

  async function handleSearch(e) {
    e.preventDefault();
    await loadMessages(activeLabel, searchQ);
  }

  async function handleDelete(msg) {
    await api.delete(`/google/gmail/threads/${msg.threadId}`);
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    if (selectedMessage?.id === msg.id) { setSelectedMessage(null); setMessageDetail(null); }
  }

  async function handleMarkRead(msg) {
    await api.patch(`/google/gmail/threads/${msg.threadId}/read`).catch(() => {});
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, unread: false } : m));
    if (selectedMessage?.id === msg.id) setSelectedMessage(prev => ({ ...prev, unread: false }));
  }

  async function handleMarkUnread(msg) {
    await api.patch(`/google/gmail/threads/${msg.threadId}/unread`).catch(() => {});
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, unread: true } : m));
    if (selectedMessage?.id === msg.id) setSelectedMessage(prev => ({ ...prev, unread: true }));
  }

  async function handleStar(msg) {
    const starred = !msg.starred;
    await api.patch(`/google/gmail/threads/${msg.threadId}/star`, { starred });
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, starred } : m));
  }

  function openReply(msg, all = false) {
    const stripped = (msg.body || '').replace(/<img[^>]*>/gi, '[이미지]');
    const quotedHtml = `<div style="border-left:3px solid #ccc;padding-left:12px;color:#666;margin:8px 0">${stripped}</div>`;
    setCompose({
      to: all ? [msg.from, ...(msg.to?.split(',') || [])].filter(e => !e.includes(sendAs[0]?.sendAsEmail)).join(', ') : msg.from,
      cc: all ? msg.cc : '',
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      body: '',
      quotedBody: quotedHtml,
      inReplyTo: msg.id,
      references: msg.threadId,
      threadId: msg.threadId,
    });
  }

  function openForward(msg) {
    const quotedHtml = `<div style="border-top:1px solid #e0e0e0;padding-top:10px;color:#555;font-size:13px"><b>---------- 전달된 메일 ----------</b><br>보낸 사람: ${msg.from}<br>날짜: ${msg.date}<br>제목: ${msg.subject}<br><br>${msg.body || ''}</div>`;
    setCompose({
      to: '',
      subject: `Fwd: ${msg.subject}`,
      body: '',
      quotedBody: quotedHtml,
    });
  }

  if (connected === false) return <GoogleConnectPrompt height="h-96" reconnect={needsReconnect} />;

  const unread = messages.filter(m => m.unread);
  const read = messages.filter(m => !m.unread);

  const renderMessage = m => (
    <div
      key={m.id}
      onClick={() => { openMessage(m); setCompose(null); }}
      className={`group px-3 py-2 border-b border-gray-800/60 cursor-pointer transition-colors hover:bg-white/[0.04] ${
        selectedMessage?.id === m.id ? 'bg-[#e0005e]/10 border-l-2 border-l-[#e0005e]' : m.unread ? 'bg-gray-800/20' : ''
      }`}
    >
      {/* 1줄: 발신자(왼) + 버튼(오른) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        {/* 왼쪽: 발신자 */}
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          {m.unread && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#e0005e' }} />}
          <span className={`text-sm min-w-0 truncate ${m.unread ? 'font-bold text-gray-100' : 'font-medium text-gray-300'}`} style={{ flex: '1 1 0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {parseFrom(m.from).name || parseFrom(m.from).email}
          </span>
        </div>
        {/* 오른쪽: 날짜(기본) + 버튼(hover 오버레이) */}
        <div className="relative flex-shrink-0 flex items-center justify-end" style={{ minWidth: 64 }}>
          {/* 기본: 날짜 + 별 아이콘 */}
          <div className="flex items-center gap-1 group-hover:opacity-0 transition-opacity">
            {m.starred && <Star size={10} fill="#facc15" style={{ color: '#facc15', flexShrink: 0 }} />}
            <span className="text-[10px] text-gray-500 whitespace-nowrap">{fmtDate(m.date)}</span>
          </div>
          {/* hover: 버튼 오버레이 */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ gap: 1 }}>
            <button onClick={e => { e.stopPropagation(); handleStar(m); }}
              className="p-1 rounded hover:bg-gray-700"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: m.starred ? '#facc15' : '#6b7280', display: 'flex' }}>
              <Star size={12} fill={m.starred ? '#facc15' : 'none'} />
            </button>
            {m.unread ? (
              <button onClick={e => { e.stopPropagation(); handleMarkRead(m); }}
                className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-blue-400"
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
                title="읽음처리">
                <MailOpen size={12} />
              </button>
            ) : (
              <button onClick={e => { e.stopPropagation(); handleMarkUnread(m); }}
                className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-blue-400"
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
                title="안읽음처리">
                <Mail size={12} />
              </button>
            )}
            <button onClick={e => { e.stopPropagation(); handleDelete(m); }}
              className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-red-400"
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>
      {/* 2줄: 제목 */}
      <p className={`text-xs truncate ${m.unread ? 'text-gray-100 font-semibold' : 'text-gray-400'}`}>{m.subject}</p>
      {/* 3줄: 스니펫 */}
      <p className="text-[11px] text-gray-500 truncate">{m.snippet}</p>
    </div>
  );

  return (
    <div className="flex overflow-hidden" style={{ width: '100%', height: '100%' }}>
      {/* 사이드바 */}
      <div className="w-44 flex-shrink-0 border-r border-gray-800 flex flex-col bg-gray-900/50">
        <div className="p-3">
          <button
            onClick={() => { setCompose({}); setSelectedMessage(null); setMessageDetail(null); }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-colors"
            style={{ background: '#e0005e', color: '#ffffff' }}
          >
            <Plus size={15} /> 새 메일 작성
          </button>
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {LABELS.map(({ id, label, icon: Icon }) => {
            const badge = id === 'INBOX' ? messages.filter(m => m.unread).length : 0;
            return (
              <button
                key={id}
                onClick={() => { setActiveLabel(id); setSearchQ(''); setCompose(null); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeLabel === id && !compose ? 'bg-[#e0005e]/15 text-[#e0005e] font-semibold' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                }`}
              >
                <Icon size={14} className="flex-shrink-0" />
                <span className="flex-1 text-left">{label}</span>
                {badge > 0 && (
                  <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none min-w-[18px] text-center" style={{ background: '#e0005e', color: '#fff' }}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* 메인 콘텐츠 */}
      {showSigModal && (
        <SignatureModal
          sendAs={sendAs}
          onSave={updated => setSendAs(prev => prev.map(s => {
            const u = updated.find(x => x.email === s.sendAsEmail);
            return u ? { ...s, signature: u.sig } : s;
          }))}
          onClose={() => setShowSigModal(false)}
        />
      )}
      {compose ? (
        /* ── 전체화면 편지 작성 ── */
        <ComposeView
          sendAs={sendAs}
          initial={compose}
          onClose={() => setCompose(null)}
          onSend={(goLabel) => { setCompose(null); setActiveLabel(goLabel || activeLabel); loadMessages(goLabel || activeLabel); }}
          onOpenSigModal={() => setShowSigModal(true)}
        />
      ) : (
        <>
          {/* 메일 목록 */}
          <div className={`flex flex-col border-r border-gray-800 min-w-0 ${selectedMessage ? 'w-72 flex-shrink-0' : 'flex-1'}`}>
            <div className="p-3 border-b border-gray-800 flex gap-2 flex-shrink-0">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="flex-1 relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    value={searchQ} onChange={e => setSearchQ(e.target.value)}
                    placeholder="메일 검색..."
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500"
                  />
                </div>
                <button type="submit" className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-xs text-gray-300">검색</button>
              </form>
              <button onClick={() => { setRefreshing(true); loadMessages(activeLabel, searchQ); }} disabled={refreshing} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-gray-300">
                <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {loading ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin text-gray-500" size={20} /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">메일이 없습니다.</div>
              ) : (
                <>
                  {unread.length > 0 && (
                    <>
                      <div className="px-4 py-1.5 flex items-center justify-between bg-gray-900 sticky top-0 z-10 border-b border-gray-800/60">
                        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#e0005e' }}>읽지 않음</span>
                        <span className="text-[10px] text-gray-500">{unread.length}개</span>
                      </div>
                      {unread.map(renderMessage)}
                    </>
                  )}
                  {read.length > 0 && (
                    <>
                      {unread.length > 0 && (
                        <div className="px-4 py-1.5 flex items-center justify-between bg-gray-900 sticky top-0 z-10 border-b border-gray-800/60">
                          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">읽은 메일</span>
                          <span className="text-[10px] text-gray-600">{read.length}개</span>
                        </div>
                      )}
                      {read.map(renderMessage)}
                    </>
                  )}
                  {nextPageToken && (
                    <button onClick={() => loadMessages(activeLabel, searchQ, nextPageToken)} disabled={loadingMore} className="w-full py-3 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800/40 transition-colors">
                      {loadingMore ? <Loader2 size={14} className="animate-spin mx-auto" /> : '더 보기'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* 메일 상세 */}
          {selectedMessage && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-800 flex-shrink-0">
                <button onClick={() => { setSelectedMessage(null); setMessageDetail(null); }} className="p-1.5 rounded hover:bg-gray-800 text-gray-400">
                  <ChevronLeft size={16} />
                </button>
                <h2 className="flex-1 text-sm font-semibold text-gray-100 truncate">{selectedMessage.subject}</h2>
                {selectedMessage.unread ? (
                  <button onClick={() => handleMarkRead(selectedMessage)} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-blue-400" title="읽음처리">
                    <MailOpen size={14} />
                  </button>
                ) : (
                  <button onClick={() => handleMarkUnread(selectedMessage)} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-blue-400" title="안읽음처리">
                    <Mail size={14} />
                  </button>
                )}
                <button onClick={() => handleDelete(selectedMessage)} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-red-400" title="휴지통">
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {messageLoading ? (
                  <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin text-gray-500" size={20} /></div>
                ) : messageDetail && (
                  <MessageCard
                    msg={messageDetail}
                    defaultOpen={true}
                    onReply={() => openReply(messageDetail)}
                    onReplyAll={() => openReply(messageDetail, true)}
                  />
                )}
              </div>

              <div className="border-t border-gray-800 px-6 py-3 flex gap-2 flex-shrink-0">
                <button onClick={() => messageDetail && openReply(messageDetail)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-800 text-sm text-gray-300 transition-colors">
                  <Reply size={13} /> 답장
                </button>
                <button onClick={() => messageDetail && openReply(messageDetail, true)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-800 text-sm text-gray-300 transition-colors">
                  <ReplyAll size={13} /> 전체 답장
                </button>
                <button onClick={() => messageDetail && openForward(messageDetail)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-800 text-sm text-gray-300 transition-colors">
                  <Forward size={13} /> 전달
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
